import PageContent from "@/components/PageContent";
import PageHeader from "@/components/PageHeader";
import Spinner from "@/components/Spinner";
import { Module } from "@/constants";
import { ProjectDetailsAndNavigation } from "@/modules/projects/components/ProjectDetailsAndNavigation";
import { BasicDataSection } from "@/modules/projects/components/settlementsDetails/section/BasicDataSection";
import { CommentsSection } from "@/modules/projects/components/settlementsDetails/section/CommentsSection";
import { InvoiceSpecificationsSection } from "@/modules/projects/components/settlementsDetails/section/InvoiceSpecificationsSection";
import { PlannedDataSection } from "@/modules/projects/components/settlementsDetails/section/PlannedDataSection";
import { ResultDataSection } from "@/modules/projects/components/settlementsDetails/section/ResultDataSection";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import { useSettlementId } from "@/modules/projects/hooks/useSettlementId";
import { ProjectService } from "@/modules/projects/service/project";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSettlementQuery } from "./hooks/useSettlementQuery";
import { useSettlementsDetailsBreadcrumb } from "./hooks/useSettlementsDetailsBreadcrumb";
import { SettlementApproveButton } from "./components/SettlementApproveButton";
import { SettlementCloseButton } from "./components/SettlementCloseButton";
import { SettlementRejectButton } from "./components/SettlementRejectButton";
import { SettlementReopenButton } from "./components/SettlementReopenButton";
import { useSettlementPermissions } from "./hooks/useSettlementPermissions";

const ProjectSettlementsDetails = () => {
  const { t } = useTranslation();

  const routeProjectId = useProjectId();
  const settlementId = useSettlementId();

  const [projectId, setProjectId] = useState<string | undefined>(
    routeProjectId,
  );

  const settlementQuery = useSettlementQuery();

  useEffect(() => {
    if (!routeProjectId && settlementQuery.data?.project_id) {
      setProjectId(settlementQuery.data.project_id);
    }
  }, [settlementQuery.data, routeProjectId]);

  const breadcrumbItems = useSettlementsDetailsBreadcrumb({
    projectId,
    settlementQuery,
  });

  const projectQuery = useQuery({
    queryKey: [Module.Projects, projectId, "settlements-details"] as const,
    queryFn: () => (projectId ? ProjectService.get(projectId) : null),
    enabled: Boolean(projectId),
  });

  const permissions = useSettlementPermissions();

  if (
    projectQuery.isLoading ||
    settlementQuery.isLoading ||
    permissions.isLoading ||
    !projectId
  ) {
    return (
      <Spinner
        label={t("projectSettlements.loading")}
        className="w-full h-full "
      />
    );
  }

  return (
    <>
      <PageHeader
        breadcrumbItems={breadcrumbItems}
        actions={[
          permissions.canApprove && <SettlementApproveButton />,
          permissions.canClose && <SettlementCloseButton />,
          permissions.canReject && <SettlementRejectButton />,
          permissions.canReopen && <SettlementReopenButton />,
        ]}
      />
      <PageContent title={t("projectSettlements.title.edit")}>
        <div className="grid grid-cols-1 grid-rows-[auto_1fr] gap-4">
          <ProjectDetailsAndNavigation projectId={projectId} />
          <BasicDataSection query={settlementQuery} />
          <PlannedDataSection query={settlementQuery} />
          <ResultDataSection query={settlementQuery} />
          <CommentsSection />
          <InvoiceSpecificationsSection
            projectId={projectId}
            settlementId={settlementId}
            projectDependsOn={projectQuery.data?.project_depends_on}
          />
        </div>
      </PageContent>
    </>
  );
};

export default ProjectSettlementsDetails;
